/**
 * The tenant log's filters, sheet 32 callout 3 (mission 7 WP11 T057; FR-014,
 * FR-015, FR-028; TX.6, UC-06; squad U10; contracts/ui-api.md "Sheet 32").
 *
 * Run, bureau, result, from, to and a free-text search, each labelled by
 * `filter.*`. The page keeps them in the URL search params and sends them to
 * the log read; this component only reports a change.
 * - The bureau list names all four bureaus in canonical order. A bureau the
 *   TX.9 kill switch names keeps its place with `disabledSuffix` (UC-06).
 * - The dates commit on change (a date input gives `YYYY-MM-DD` or nothing).
 * - The run id and the search text commit on Enter or when the field loses
 *   focus, so a half-typed id is not sent on every keystroke. They are
 *   uncontrolled, keyed on the committed value, and the search text is put in
 *   no `aria-*` or `data-*` attribute; it goes only to the read's query string
 *   (and the page's own `q` param).
 */
import type { KeyboardEvent } from 'react'

import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select'
import { BUREAUS, bureauName, type Bureau } from '@/lib/api/bureaus'
import { TRANSMISSION_RESULTS, type TransmissionFilters, type TransmissionResult } from '@/lib/api/transmissions'
import { copyText, type TextKey } from '@/lib/provisional-copy'

export type FilterKey = keyof TransmissionFilters

const RESULT_LABEL: Record<TransmissionResult, TextKey> = {
  pending: 'resultLabel.pending',
  success: 'resultLabel.success',
  auth_failed: 'resultLabel.authFailed',
  timeout: 'resultLabel.timeout',
  not_sent: 'resultLabel.notSent',
}

/** The select's "no filter" item, as mission 6's run history labels it. */
const ALL = 'all'

export interface LogFiltersProps {
  filters: TransmissionFilters
  /** Bureaus the kill switch names (`disabled_by_trellis`), from `bureausQuery()`. */
  disabled: ReadonlySet<Bureau>
  onChange(key: FilterKey, value: string | undefined): void
}

interface ChoiceProps {
  id: string
  label: string
  value: string | undefined
  options: ReadonlyArray<[value: string, label: string]>
  onChange(value: string | undefined): void
}

function Choice({ id, label, value, options, onChange }: ChoiceProps) {
  return (
    <div className="flex w-48 flex-col gap-1.5">
      <Label htmlFor={id}>{label}</Label>
      <Select value={value ?? ALL} onValueChange={(v) => onChange(v === ALL ? undefined : v)}>
        <SelectTrigger id={id}>
          <SelectValue />
        </SelectTrigger>
        <SelectContent>
          {/* mission 7 WP14: WP11 deviation 2, the no-filter item from the register. */}
          <SelectItem value={ALL}>{copyText('filter.all')}</SelectItem>
          {options.map(([v, text]) => (
            <SelectItem key={v} value={v}>
              {text}
            </SelectItem>
          ))}
        </SelectContent>
      </Select>
    </div>
  )
}

interface TextFilterProps {
  id: string
  label: string
  value: string | undefined
  type: 'text' | 'search'
  maxLength: number
  mono?: boolean
  onCommit(value: string | undefined): void
}

function TextFilter({ id, label, value, type, maxLength, mono = false, onCommit }: TextFilterProps) {
  function commit(raw: string) {
    const next = raw.trim() === '' ? undefined : raw.trim()
    if (next !== value) onCommit(next)
  }
  function onKeyDown(event: KeyboardEvent<HTMLInputElement>) {
    if (event.key === 'Enter') {
      event.preventDefault()
      commit(event.currentTarget.value)
    }
  }
  return (
    <div className="flex w-72 flex-col gap-1.5">
      <Label htmlFor={id}>{label}</Label>
      <Input
        key={value ?? ''}
        id={id}
        type={type}
        defaultValue={value ?? ''}
        maxLength={maxLength}
        spellCheck={false}
        autoComplete="off"
        className={mono ? 'font-mono' : undefined}
        onKeyDown={onKeyDown}
        onBlur={(event) => commit(event.currentTarget.value)}
      />
    </div>
  )
}

function DateFilter({ id, label, value, onChange }: { id: string; label: string; value: string | undefined; onChange(v: string | undefined): void }) {
  return (
    <div className="flex w-44 flex-col gap-1.5">
      <Label htmlFor={id}>{label}</Label>
      <Input
        id={id}
        type="date"
        className="font-mono"
        value={value ?? ''}
        onChange={(event) => onChange(event.target.value === '' ? undefined : event.target.value)}
      />
    </div>
  )
}

export function LogFilters({ filters, disabled, onChange }: LogFiltersProps) {
  return (
    <div role="search" className="flex flex-wrap items-end gap-4">
      <TextFilter
        id="log-run"
        label={copyText('filter.run')}
        value={filters.run}
        type="text"
        maxLength={36}
        mono
        onCommit={(v) => onChange('run', v)}
      />
      <Choice
        id="log-bureau"
        label={copyText('filter.bureau')}
        value={filters.bureau}
        options={BUREAUS.map((b) => [b, disabled.has(b) ? `${bureauName(b)} ${copyText('disabledSuffix')}` : bureauName(b)])}
        onChange={(v) => onChange('bureau', v)}
      />
      <Choice
        id="log-result"
        label={copyText('filter.result')}
        value={filters.result}
        options={TRANSMISSION_RESULTS.map((r) => [r, copyText(RESULT_LABEL[r])])}
        onChange={(v) => onChange('result', v)}
      />
      <DateFilter id="log-from" label={copyText('filter.from')} value={filters.from} onChange={(v) => onChange('from', v)} />
      <DateFilter id="log-to" label={copyText('filter.to')} value={filters.to} onChange={(v) => onChange('to', v)} />
      <TextFilter
        id="log-q"
        label={copyText('filter.search')}
        value={filters.q}
        type="search"
        maxLength={64}
        onCommit={(v) => onChange('q', v)}
      />
    </div>
  )
}
